/**
 * Recovery Loop stepper model — maps a case's display state to the horizontal
 * lifecycle track (the rows in the mock). Pure + unit-tested so the visual logic
 * is verified without rendering.
 *
 * Happy path: SMS Sent → Waiting → Card Received → Extracted → Re-run → Resolved.
 * Error paths short-circuit to a red terminal (No Phone / Staff Review), and
 * opt-out renders muted. `Re-run`/`Resolved` stay pending until the step-6
 * re-check lands.
 */
import type { RecoveryDisplayState } from '../services/recoveryCases'

/** Colour band for a step's node + label. */
export type StepTone = 'done' | 'current' | 'pending' | 'error' | 'muted'
/** Which glyph the node draws. */
export type StepIcon = 'check' | 'x' | 'ring'

export interface RecoveryStep {
  label: string
  tone: StepTone
  icon: StepIcon
}

const done = (label: string): RecoveryStep => ({ label, tone: 'done', icon: 'check' })
const current = (label: string): RecoveryStep => ({ label, tone: 'current', icon: 'ring' })
const pending = (label: string): RecoveryStep => ({ label, tone: 'pending', icon: 'ring' })
const error = (label: string): RecoveryStep => ({ label, tone: 'error', icon: 'x' })
const mutedCheck = (label: string): RecoveryStep => ({ label, tone: 'muted', icon: 'check' })
const mutedX = (label: string): RecoveryStep => ({ label, tone: 'muted', icon: 'x' })

/**
 * The road every case travels, always the same six stops.
 *
 * A failed case shows the failed step in red and THE REST OF THE ROAD STILL
 * VISIBLE — it never collapses to a couple of red dots. That was the PO's
 * review point (2026-07-13): "Frank's and Teresa's show two red dots and
 * nothing else. Every case should read like Harper's… Staff should see at a
 * glance where the journey stopped, not reconstruct it." A truncated track
 * hides how far the loop got, which is exactly what staff need to judge what's
 * left to do by hand.
 */
const ROAD = ['SMS Sent', 'Waiting', 'Card Received', 'Extracted', 'Re-run', 'Resolved'] as const

/**
 * The six-stop road with everything before `failedAt` done, that stop failed,
 * and the remaining stops shown as never-reached (pending, not erased).
 * `tone` for the unreached tail is 'pending' so it reads as road-not-travelled
 * rather than as something still in flight — the failed stop is the terminal.
 */
function roadStoppedAt(failedAt: number, labels: readonly string[] = ROAD): RecoveryStep[] {
  return labels.map((label, i) => {
    if (i < failedAt) return done(label)
    if (i === failedAt) return error(label)
    return pending(label)
  })
}

/** Same shape, but for a track that ended without failing (opted out) — the
 *  stop that ended it wears a muted x and the tail stays visible. */
function roadMutedAt(endedAt: number, endLabel?: string): RecoveryStep[] {
  return ROAD.map((label, i) => {
    if (i < endedAt) return mutedCheck(label)
    if (i === endedAt) return mutedX(endLabel ?? label)
    return pending(label)
  })
}

/** The stepper stages for a case, in order. */
export function buildRecoverySteps(state: RecoveryDisplayState): RecoveryStep[] {
  switch (state) {
    case 'sms_sent':
      return [done('SMS Sent'), current('Waiting'), pending('Card Received'), pending('Extracted'), pending('Re-run'), pending('Resolved')]
    case 'needs_clarification':
      return [done('SMS Sent'), done('Waiting'), done('Card Received'), current('Follow-up'), pending('Re-run'), pending('Resolved')]
    case 'awaiting_reply':
      return [done('SMS Sent'), done('Waiting'), done('Card Received'), current('Awaiting reply'), pending('Re-run'), pending('Resolved')]
    case 'extracted':
      return [done('SMS Sent'), done('Waiting'), done('Card Received'), done('Extracted'), current('Re-run'), pending('Resolved')]
    case 'check_rerun':
      return [done('SMS Sent'), done('Waiting'), done('Card Received'), done('Extracted'), current('Re-run'), pending('Resolved')]
    case 'resolved':
      return [done('SMS Sent'), done('Waiting'), done('Card Received'), done('Extracted'), done('Re-run'), done('Resolved')]
    case 'extracted_same':
      // The card matched what we already had, so the journey STOPPED at the
      // re-run — it never reached Resolved. Labelling the last stop "Resolved"
      // (as this used to) claimed a recovery that didn't happen; the muted x
      // says "ended here, nothing broke, nothing fixed".
      return roadMutedAt(4, 'No change')
    // ── Failure paths: the road stops, but the road still shows ──
    case 'rerun_failed':
      // Got all the way to the re-run and it didn't verify.
      return roadStoppedAt(4)
    case 'cannot_extract':
      // Card arrived but couldn't be read — the journey dies at Extracted.
      return roadStoppedAt(3)
    case 'escalated':
      // #304: a person is needed. The journey stops at Waiting, because that is
      // where the conversation actually broke down.
      return roadStoppedAt(1)
    case 'sms_failed':
      // The text itself never landed: step one failed, nothing after it ran.
      return roadStoppedAt(0)
    case 'no_phone':
      // Never even started — same shape, and the band says why.
      return roadStoppedAt(0)
    case 'opted_out':
      // Not a failure: the patient declined, so the track is muted rather than
      // red. It still shows how far we got before they said STOP.
      return roadMutedAt(1, 'Opted Out')
  }
}

/** The status-summary bucket a case belongs to (filter chips / KPI tiles). */
export type RecoveryBucket =
  | 'waiting'          // texted, awaiting the patient
  | 'in_progress'      // card in, being read/re-run
  | 'staff_review'     // needs a human
  | 'recovered'        // the loop actually fixed it
  | 'closed_by_staff'  // a human gave up on it and closed it by hand
  | 'opted_out'        // patient declined

/**
 * A case the loop recovered and a case a human gave up on are DIFFERENT CLAIMS
 * and must never share a bucket. "The loop closed 12 cases this week" is a
 * number we put in front of customers; padding it with dead ends the loop never
 * touched would make the whole metric a lie (PO, 2026-07-13). So a hand-closed
 * case files under `closed_by_staff` — the stepper still shows its real journey,
 * and the counts stay honest.
 */
export function recoveryBucket(state: RecoveryDisplayState, handled = false): RecoveryBucket {
  if (handled && state !== 'resolved') return 'closed_by_staff'
  switch (state) {
    case 'sms_sent':
    case 'awaiting_reply':
      return 'waiting'
    case 'needs_clarification':
    case 'extracted':
    case 'check_rerun':
      return 'in_progress'
    case 'escalated':
    case 'cannot_extract':
    case 'sms_failed':
    case 'no_phone':
    case 'rerun_failed':
      return 'staff_review'
    case 'resolved':
      return 'recovered'
    case 'extracted_same':
      // The card matched what we already had, so the loop resolved nothing —
      // it's closed, but it isn't a recovery we can claim.
      return 'closed_by_staff'
    case 'opted_out':
      return 'opted_out'
  }
}

export const RECOVERY_BUCKET_LABEL: Record<RecoveryBucket, string> = {
  waiting: 'Waiting on patient',
  in_progress: 'In progress',
  staff_review: 'Staff review',
  recovered: 'Recovered',
  closed_by_staff: 'Closed by staff',
  opted_out: 'Opted out',
}

/**
 * The single hue a case's whole track + status chip wear. The design's rule:
 * the color says who/what the case is waiting on right now — amber = the
 * patient, blue/indigo = our automation, green = closed, red = a human here.
 */
export type RecoveryHue = 'amber' | 'blue' | 'indigo' | 'green' | 'red' | 'muted'

export function recoveryHue(state: RecoveryDisplayState): RecoveryHue {
  switch (state) {
    // #304: a human is needed here, which is what red means.
    case 'escalated':
      return 'red'
    case 'sms_sent':
    case 'awaiting_reply':
      return 'amber'
    case 'needs_clarification':
      return 'blue'
    case 'extracted':
    case 'check_rerun':
      return 'indigo'
    case 'resolved':
      return 'green'
    case 'extracted_same':
      // Muted, not green: nothing was recovered, so the track must not wear the
      // success hue next to cases the loop actually fixed.
      return 'muted'
    case 'cannot_extract':
    case 'sms_failed':
    case 'no_phone':
    case 'rerun_failed':
      return 'red'
    case 'opted_out':
      return 'muted'
  }
}

/**
 * Row/slideout status chip — finer-grained than the filter buckets (splits
 * "In progress" into Extracting / Re-running), always in the case hue.
 */
export function recoveryStatusChip(
  state: RecoveryDisplayState,
  handled = false,
): { label: string; hue: RecoveryHue } {
  // Hand-closed but never recovered: gray and named for what it is, so the row
  // never wears a green "win" badge the loop didn't earn.
  if (handled && state !== 'resolved') return { label: 'Closed by staff', hue: 'muted' }
  const label = (() => {
    switch (state) {
      case 'sms_sent':
        return 'Waiting on patient'
      case 'awaiting_reply':
        return 'Awaiting reply'
      case 'needs_clarification':
        return 'Following up'
      case 'extracted':
      case 'check_rerun':
        return 'Re-running'
      case 'resolved':
        return 'Recovered'
      case 'extracted_same':
        return 'No change'
      case 'escalated':
        // Distinguishable at a glance from a case merely waiting on a reply.
        return 'Needs a person'
      case 'cannot_extract':
      case 'sms_failed':
      case 'no_phone':
      case 'rerun_failed':
        return 'Staff review'
      case 'opted_out':
        return 'Opted out'
    }
  })()
  return { label, hue: recoveryHue(state) }
}
